/**
 * `set_letterhead` — poser ou retirer l'en-tête du cabinet, à la demande de l'agent.
 *
 * ## Ce que cet exécuteur ne fait PAS
 *
 * Il ne réimplémente aucune géométrie d'en-tête. Tout le geste vit déjà dans
 * `editor/canvasLetterhead.ts`, et ce module n'en est que la porte agent : c'est
 * là que sont l'ordre load-bearing (l'image est LUE avant que quoi que ce soit
 * ne soit retiré — sinon une image corrompue laisse un document sans en-tête du
 * tout), l'orphelinat relu avant suppression d'une partie partagée, et le refus
 * sur en-tête hérité de la variante `default`. Deux implémentations de cette
 * séquence divergeraient au premier correctif, et le versant agent serait le
 * dernier à en profiter.
 *
 * ## La confirmation a changé de lieu, pas de nature
 *
 * Côté avocat, écraser un en-tête existant demande un `AlertDialog`, parce que
 * la garde d'idempotence est structurellement ANONYME : `HeaderFooterSlotEntry`
 * ne porte aucune provenance, donc un logo importé avec le `.docx` de l'avocat
 * et un logo posé par Hector sont indiscernables. Un exécuteur ne peut pas
 * ouvrir de dialogue. La confirmation se déplace donc dans la CONVERSATION : la
 * première passe reste additive, un en-tête déjà présent produit un refus AVANT
 * toute mutation qui nomme le coût, et l'agent ne repasse `replace_existing`
 * qu'après un oui de l'avocat. L'invariant « rien n'est écrasé sans
 * consentement » tient ; c'est son support qui change.
 *
 * ## Le premier verbe à latence réseau
 *
 * Les vingt autres transforment le document en mémoire. Celui-ci demande
 * d'abord au serveur la liste des en-têtes, puis télécharge une image — d'où la
 * ré-assertion d'époque APRÈS l'attente, avant la moindre écriture : le canvas
 * peut avoir été remplacé pendant le trajet. Et un rejet réseau n'est pas un
 * refus métier : « ce cabinet n'a pas cet en-tête » et « on n'a pas pu
 * demander » sont deux accusés distincts, que l'agent ne doit pas confondre —
 * le second se retente, le premier jamais.
 */

import {
  abortedBeforeMutation,
  buildVerifiedReceipt,
  toCanvasSnapshotSummary,
  type CanvasActionReceipt,
  type VerificationCheck,
} from './actionReceipt';
import { idsDeRevision } from './canvasSnapshot';
import { revisionCourante } from './fencesDeMutation';
import { snapshot, type CanvasActionContext } from './textActionExecutors';
import {
  appliquerLEnTeteDuCabinet,
  lireLEtatDeLEnTete,
  retirerLEnTeteDuCabinet,
  type DocumentApiEnTete,
  type EtatDeLEnTete,
} from '../../editor/canvasLetterhead';
import type { EnTeteResolu } from '../canvasEditorStore';
import type { CanvasActionInput } from '@hector/shared-types';

export type SetLetterheadInput = Extract<CanvasActionInput, { action: 'set_letterhead' }>;

/**
 * Le `doc` du contexte, vu par le module d'en-tête.
 *
 * Les deux décrivent le même objet SuperDoc avec des familles différentes ;
 * `canvasLetterhead.ts` déclare les siennes de façon structurellement
 * compatible, donc la conversion est une simple vue et non une assertion sur du
 * vide.
 */
function docDEnTete(ctx: CanvasActionContext): DocumentApiEnTete {
  return ctx.doc as unknown as DocumentApiEnTete;
}

/** Le refus rédigé une fois, pour les cinq motifs qui n'écrivent rien. */
function refus(
  input: SetLetterheadInput,
  resume: Awaited<ReturnType<typeof snapshot>>,
  code: string,
  message: string,
  recovery: NonNullable<CanvasActionReceipt['recovery']>,
): CanvasActionReceipt {
  return abortedBeforeMutation({
    intent: input.action,
    snapshot: toCanvasSnapshotSummary(resume),
    code,
    message,
    recovery,
  });
}

/** Traduit une résolution ratée en refus nommé — chaque issue a sa sortie. */
function refusDeResolution(
  input: SetLetterheadInput,
  resume: Awaited<ReturnType<typeof snapshot>>,
  resolu: Exclude<EnTeteResolu, { issue: 'trouve' }>,
): CanvasActionReceipt {
  if (resolu.issue === 'ambigu') {
    return refus(
      input,
      resume,
      'LETTERHEAD_AMBIGUOUS',
      `set_letterhead a été REFUSÉ avant toute écriture : « ${input.name ?? ''} » désigne plusieurs en-têtes `
      + `(${resolu.correspondances.join(', ')}). Le document est intact. Poser le mauvais papier sur un acte `
      + 'déposé au greffe est un dégât que l\'avocat ne voit qu\'après coup : demande-lui lequel il veut, puis '
      + 'rappelle ce verbe avec le nom exact.',
      { action: 'stop', reason: 'le nom donné désigne plusieurs en-têtes — seul l\'avocat peut trancher' },
    );
  }
  if (resolu.issue === 'sans-fichier') {
    return refus(
      input,
      resume,
      'LETTERHEAD_WITHOUT_FILE',
      `set_letterhead a été REFUSÉ avant toute écriture : l'en-tête « ${resolu.nom} » existe mais aucun fichier `
      + 'n\'y a été déposé, il n\'y a donc aucun logo à poser. Le document est intact. Dis à l\'avocat de déposer '
      + 'son papier à en-tête dans Personnalisation → Charte graphique → En-tête et pied de page. Relancer ce verbe échouera à '
      + 'l\'identique.',
      { action: 'stop', reason: 'aucun fichier déposé pour cet en-tête' },
    );
  }
  const disponibles = resolu.disponibles.length > 0
    ? `En-têtes disponibles : ${resolu.disponibles.join(', ')}.`
    : 'Ce cabinet n\'a enregistré aucun en-tête.';
  return refus(
    input,
    resume,
    'LETTERHEAD_NOT_FOUND',
    `set_letterhead a été REFUSÉ avant toute écriture : aucun en-tête ne correspond à `
    + `« ${input.name ?? '(défaut)'} ». Le document est intact. ${disponibles} `
    + 'Reprends un nom de cette liste — n\'invente pas, et n\'essaie pas un identifiant : la désignation se fait '
    + 'uniquement par le nom.',
    { action: 'stop', reason: 'nom d\'en-tête inconnu du cabinet' },
  );
}

/**
 * La postcondition — et pourquoi elle ne compte rien.
 *
 * `CanvasSnapshot` ne porte AUCUN domaine d'en-tête : l'en-tête vit dans une
 * partie séparée du document (`word/headerN.xml`), pas dans le corps. La
 * relecture passe donc par un appel dédié, `lireLEtatDeLEnTete`.
 *
 * ⚠️ Un état `null` n'est PAS un échec. Il dit que le moteur n'expose pas de
 * quoi répondre — la même doctrine que `controleDePose` pour les notes : une
 * surface absente rend le même vide qu'un vrai vide, et seule la PRÉSENCE de la
 * surface distingue les deux. Rendre `passed: false` sur une lecture ratée
 * ferait échouer une pose parfaitement réussie, et l'agent la rejouerait —
 * posant un second en-tête par-dessus le premier.
 */
function controleDePoseDEnTete(etat: EtatDeLEnTete, attendu: 'pose' | 'aucun'): VerificationCheck {
  if (etat === null) {
    return {
      name: 'en_tete_relu',
      passed: true,
      message: 'Cet éditeur ne rend pas l\'état de son en-tête de première page : seule la réponse du moteur '
        + 'atteste le geste, aucune relecture ne le prouve. Ne l\'annonce pas à l\'avocat comme vérifié.',
    };
  }
  const conforme = etat === attendu;
  return {
    name: attendu === 'pose' ? 'en_tete_pose' : 'en_tete_retire',
    passed: conforme,
    message: conforme
      ? attendu === 'pose'
        ? 'Relecture : la première page porte bien un en-tête explicite.'
        : 'Relecture : la première page ne porte plus aucun en-tête.'
      : `set_letterhead a été annoncé en succès par l'éditeur, mais la relecture rend « ${etat} » là où `
        + `« ${attendu} » était attendu. Relis le document avant toute autre action — ne rejoue pas ce verbe à `
        + 'l\'identique, il poserait un second en-tête si le premier avait bien été enregistré.',
  };
}

/**
 * Pose ou retire l'en-tête du cabinet sur la première page.
 *
 * @param input - `name` absent = l'en-tête par défaut. `remove` et
 *   `replace_existing` sont exclusifs par la règle inter-champs du catalogue.
 */
export async function executeSetLetterhead(
  ctx: CanvasActionContext,
  input: SetLetterheadInput,
): Promise<CanvasActionReceipt> {
  const pre = await snapshot(ctx);
  const doc = docDEnTete(ctx);

  if (input.remove === true) return retirer(ctx, input, pre, doc);

  // `undefined` = aucun canvas monté, donc personne pour aller chercher les
  // octets du logo. Refuser ici plutôt que plus loin garde le document intact et
  // dit la vraie cause, au lieu d'un « sans-image » qui enverrait l'agent
  // conseiller à l'avocat de déposer un fichier qu'il a peut-être déjà.
  if (!ctx.resoudreLEnTeteNomme) {
    return refus(
      input,
      pre,
      'LETTERHEAD_RESOLVER_UNAVAILABLE',
      'set_letterhead est indisponible : aucun canvas de rédaction n\'est monté pour aller chercher les en-têtes '
      + 'du cabinet. RIEN n\'a été écrit. Ouvre le canvas, puis rappelle ce verbe.',
      { action: 'stop', reason: 'aucun canvas monté' },
    );
  }

  return poser(ctx, input, pre, doc, ctx.resoudreLEnTeteNomme);
}

async function retirer(
  ctx: CanvasActionContext,
  input: SetLetterheadInput,
  pre: Awaited<ReturnType<typeof snapshot>>,
  doc: DocumentApiEnTete,
): Promise<CanvasActionReceipt> {
  const revisionsAvant = idsDeRevision(pre.trackedChanges);
  const revisionAvant = await revisionCourante(ctx, pre.revision);

  ctx.verifierEpoque('set_letterhead:retrait');
  const resultat = await ctx.mutate(
    () => retirerLEnTeteDuCabinet(doc),
    { changeMode: 'direct', expectedRevision: revisionAvant },
  );

  if (resultat !== 'retire') {
    const message = resultat === 'aucun-en-tete'
      ? 'set_letterhead (remove) n\'a RIEN eu à retirer : la première page de ce document ne porte aucun en-tête. '
        + 'Le document est intact. Ne rejoue pas ce verbe — dis simplement à l\'avocat qu\'il n\'y en avait pas.'
      : resultat === 'en-tete-herite'
        ? 'set_letterhead (remove) a été REFUSÉ : l\'en-tête de ce document n\'est pas propre à la première page, '
          + 'il est hérité et s\'affiche sur TOUTES les pages. Le retirer par ce chemin ne retirerait rien tout en '
          + 'annonçant un succès. Le document est intact. Dis à l\'avocat que cet en-tête vient de son document '
          + 'Word et doit s\'y modifier.'
        : resultat === 'api-indisponible'
          ? 'set_letterhead (remove) est indisponible sur ce document : l\'éditeur n\'expose pas les opérations '
            + 'de détachement d\'en-tête. Ce n\'est pas un échec passager — relancer échouera à l\'identique.'
          : 'set_letterhead (remove) a été REFUSÉ par l\'éditeur et RIEN n\'a été retiré. Le document est intact. '
            + 'Relis-le avant toute autre action.';
    return refus(input, pre, `LETTERHEAD_${resultat.toUpperCase().replace(/-/g, '_')}`, message, {
      action: 'stop',
      reason: resultat,
    });
  }

  const post = await snapshot(ctx);
  ctx.verifierEpoque('set_letterhead:receipt');
  const etat = await lireLEtatDeLEnTete(doc);

  return buildVerifiedReceipt({
    intent: input.action,
    regimeSuivi: ctx.regimeSuivi,
    preSnapshot: toCanvasSnapshotSummary(pre),
    postSnapshot: toCanvasSnapshotSummary(post),
    resolvedTargets: [],
    provider: resultat,
    revisionsAvant,
    revisionsApres: idsDeRevision(post.trackedChanges),
    revisionAvant,
    revisionApres: await revisionCourante(ctx, post.revision),
    // Un en-tête vit hors du corps suivi : aucune révision ne naît de ce geste,
    // et en attendre une ferait rendre `partial` sur un retrait parfait.
    mouvementAttendu: 'aucun',
    verification: [controleDePoseDEnTete(etat, 'aucun')],
    effects: [{
      kind: 'set_letterhead',
      reviewability: 'direct_untracked',
      description: 'En-tête de première page retiré, et sa partie orpheline supprimée.',
    }],
  });
}

async function poser(
  ctx: CanvasActionContext,
  input: SetLetterheadInput,
  pre: Awaited<ReturnType<typeof snapshot>>,
  doc: DocumentApiEnTete,
  resoudre: NonNullable<CanvasActionContext['resoudreLEnTeteNomme']>,
): Promise<CanvasActionReceipt> {
  let resolu: EnTeteResolu;
  try {
    resolu = await resoudre(input.name ?? null);
  } catch (erreur) {
    // ⚠️ Un rejet réseau n'est PAS « cet en-tête n'existe pas ». Le confondre
    // enverrait l'agent annoncer à l'avocat que son cabinet n'a pas d'en-tête
    // alors qu'il en a un et que la requête a simplement échoué.
    return refus(
      input,
      pre,
      'LETTERHEAD_LOOKUP_FAILED',
      'set_letterhead n\'a pas pu lire les en-têtes du cabinet : la requête a échoué '
      + `(${erreur instanceof Error ? erreur.message : String(erreur)}). RIEN n'a été écrit et le document est `
      + 'intact. Ce n\'est pas « ce cabinet n\'a pas d\'en-tête » — on n\'a pas pu le demander. Réessaie une fois ; '
      + 'si l\'échec persiste, dis-le à l\'avocat.',
      { action: 'retry', reason: 'la lecture des en-têtes du cabinet a échoué' },
    );
  }

  // ⚠️ Le document a pu être remplacé pendant l'attente réseau — c'est le seul
  // verbe où cette fenêtre existe, et elle dure le temps d'un téléchargement.
  ctx.verifierEpoque('set_letterhead:apres-resolution');

  if (resolu.issue !== 'trouve') return refusDeResolution(input, pre, resolu);

  const revisionsAvant = idsDeRevision(pre.trackedChanges);
  const revisionAvant = await revisionCourante(ctx, pre.revision);

  ctx.verifierEpoque('set_letterhead:pose');
  const geste = await ctx.mutate(
    () => appliquerLEnTeteDuCabinet(doc, resolu.entree, { confirmeParLAvocat: input.replace_existing === true }),
    { changeMode: 'direct', expectedRevision: revisionAvant },
  );

  if (geste.etat === 'confirmation-requise') {
    return refus(
      input,
      pre,
      'LETTERHEAD_ALREADY_PRESENT',
      `set_letterhead a été REFUSÉ avant toute écriture : la première page de ce document porte DÉJÀ un en-tête, `
      + `et « ${resolu.nom} » ne l'a pas remplacé. Le document est intact. L'éditeur ne sait pas dire d'où vient `
      + 'l\'en-tête présent : il peut venir d\'un document Word que l\'avocat a importé, auquel cas il sera PERDU. '
      + 'Demande-lui explicitement s\'il veut le remplacer, en lui disant cela, puis rappelle ce verbe avec '
      + 'replace_existing. N\'utilise jamais replace_existing sans avoir posé la question.',
      { action: 'stop', reason: 'un en-tête est déjà présent — l\'avocat doit consentir à son remplacement' },
    );
  }

  if (geste.resultat !== 'pose' && geste.resultat !== 'remplace') {
    const message = geste.resultat === 'desactive'
      ? `set_letterhead n'a RIEN posé : l'en-tête « ${resolu.nom} » est désactivé dans les réglages du cabinet. `
        + 'Le document est intact. Dis à l\'avocat de l\'activer dans Personnalisation → Charte graphique.'
      : geste.resultat === 'sans-image'
        ? `set_letterhead n'a RIEN posé : aucun fichier n'est associé à « ${resolu.nom} ». Le document est intact.`
        : geste.resultat === 'api-indisponible'
          ? 'set_letterhead est indisponible sur ce document : l\'éditeur n\'expose pas les opérations d\'en-tête. '
            + 'Relancer échouera à l\'identique.'
          : geste.resultat === 'image-illisible'
            ? `set_letterhead n'a RIEN posé : le fichier de « ${resolu.nom} » n'a pas pu être lu comme une image. `
              + 'Le document est intact. Dis à l\'avocat de redéposer son logo.'
            : 'set_letterhead a été REFUSÉ par l\'éditeur et RIEN n\'a été posé. Relis le document avant toute '
              + 'autre action.';
    return refus(input, pre, `LETTERHEAD_${geste.resultat.toUpperCase().replace(/-/g, '_')}`, message, {
      action: 'stop',
      reason: geste.resultat,
    });
  }

  const post = await snapshot(ctx);
  ctx.verifierEpoque('set_letterhead:receipt');
  const etat = await lireLEtatDeLEnTete(doc);

  return buildVerifiedReceipt({
    intent: input.action,
    regimeSuivi: ctx.regimeSuivi,
    preSnapshot: toCanvasSnapshotSummary(pre),
    postSnapshot: toCanvasSnapshotSummary(post),
    resolvedTargets: [],
    provider: geste.resultat,
    revisionsAvant,
    revisionsApres: idsDeRevision(post.trackedChanges),
    revisionAvant,
    revisionApres: await revisionCourante(ctx, post.revision),
    mouvementAttendu: 'aucun',
    verification: [controleDePoseDEnTete(etat, 'pose')],
    effects: [{
      kind: 'set_letterhead',
      reviewability: 'direct_untracked',
      description: geste.resultat === 'remplace'
        ? `En-tête « ${resolu.nom} » posé en première page, EN REMPLACEMENT de celui qui s'y trouvait.`
        : `En-tête « ${resolu.nom} » posé en première page.`,
    }],
  });
}
